import React from 'react';
import PhoneIcon from '@material-ui/icons/Phone';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {makeStyles} from '@material-ui/core/styles';
import {Textsms} from '@material-ui/icons';

const PHONE_NUMBER = '0482099121';
const PHONE_DISPLAY = '0482 099 121';

function isTouchDevice() {
  return navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'fixed',
    bottom: '1rem',
    right: '1.5rem',
    zIndex: 99999,
    [theme.breakpoints.down('sm')]: {
      right: 'auto',
      left: '16px',
    },
  },
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px 10px 10px',
    borderRadius: '40px',
    backgroundColor: theme.palette.secondary.dark,
    color: theme.palette.common.white,
    textDecoration: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    transition: 'transform 150ms ease, box-shadow 150ms ease',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.25)',
    '&:hover': {
      color: theme.palette.common.white,
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
    },
    '&:active': {
      transform: 'scale(0.97)',
      boxShadow: '0 1px 6px rgba(0, 0, 0, 0.2)',
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.secondary.main}`,
      outlineOffset: '3px',
    },
  },
  iconCircle: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textStack: {
    display: 'flex',
    flexDirection: 'column',
    '& > :first-child': {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      opacity: 0.85,
    },
    '& > :last-child': {
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
  },
  textRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  copyIcon: {
    marginLeft: '2px',
    opacity: 0.6,
    fontSize: '18px !important',
    transition: 'opacity 150ms ease',
    '$root:hover &': {
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileCard: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '30px',
    backgroundColor: theme.palette.secondary.dark,
    color: theme.palette.common.white,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minWidth: '230px',
  },
  mobileHeader: {
    textAlign: 'center',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '4px 20px',
    opacity: 0.9,
  },
  mobileDividerH: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    margin: '0 16px',
  },
  mobileActions: {
    display: 'flex',
    alignItems: 'stretch',
  },
  mobileIconCircle: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: '14px',
    },
  },
  mobileDividerV: {
    width: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    flexShrink: 0,
  },
  mobileAction: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '7px 16px',
    textDecoration: 'none',
    color: theme.palette.common.white,
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background-color 100ms ease',
    '&:active': {
      backgroundColor: 'rgba(255, 255, 255, 0.18)',
      color: theme.palette.common.white,
    },
    '&:hover': {
      color: theme.palette.common.white,
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.secondary.main}`,
      outlineOffset: '-3px',
    },
  },
}));

const PhoneHelpBar = () => {
  const classes = useStyles();
  const isProvider = useSelector(state => state.auth.isProvider);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const handleClick = async (e) => {
    if (isTouchDevice() && isMobile) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(PHONE_NUMBER);
      toast.success('Phone number copied!');
    } catch {
      toast.error('Failed to copy phone number');
    }
  };

  if (isProvider) return null;

  if (isTouchDevice() && isMobile) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.mobileCard} role='group' aria-label='Get help finding support'>
          <div className={classes.mobileHeader}>Get Help Finding Support</div>
          <div className={classes.mobileDividerH} />
          <div className={classes.mobileActions}>
            <a
              href={`tel:${PHONE_NUMBER}`}
              className={classes.mobileAction}
              aria-label={`Call ${PHONE_DISPLAY}`}
            >
              <span className={classes.mobileIconCircle}><PhoneIcon /></span>
              <span>Call</span>
            </a>
            <div className={classes.mobileDividerV} />
            <a
              href={`sms:${PHONE_NUMBER}`}
              className={classes.mobileAction}
              aria-label={`Text ${PHONE_DISPLAY}`}
            >
              <span className={classes.mobileIconCircle}><Textsms /></span>
              <span>Text</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <a
        href={`tel:${PHONE_NUMBER}`}
        onClick={handleClick}
        className={classes.root}
        aria-label={`Get help finding support. Call or text ${PHONE_DISPLAY}`}
      >
        <span className={classes.iconCircle}><PhoneIcon fontSize='small' /></span>
        <span className={classes.textStack}>
          <span>Get Help Finding Support</span>
          <span className={classes.textRow}>
            <span>{PHONE_DISPLAY}</span>
            <span>(Call or Text)</span>
          </span>
        </span>
        <FileCopyOutlinedIcon className={classes.copyIcon} titleAccess='Copy phone number' />
      </a>
    </div>
  );
};

export default PhoneHelpBar;
